import {
  Fragment,
  useRef,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from 'react'
import { Dialog, Combobox, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { AgGridReact } from 'ag-grid-react'
import { useSearchParams } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'

import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/20/solid'
import { PlusCircleIcon } from '@heroicons/react/24/solid'

import { EditableComponent, AgGridActionCellRenderer } from '../components'

const gradingMethods = [
  {
    id: 'GRADED_WITH_CONFIDENCE_SCORE',
    name: 'Confidence Score',
  },
]

const DialogEditKnowledgeCheck = ({
  open = false,
  inProgress = false,
  title = '',
  text = '',
  yesText = 'Yes',
  noText = 'No',
  inProgressButtonText = 'Please wait...',
  rowData = [],
  setRowData = () => {},
  setOpen = () => {},
  onConfirm = () => {},
  onCancel = () => {},
}) => {
  let [searchParams] = useSearchParams()
  const qid = searchParams.get('q')
  const { id, statement, choices, point, feedback } = qid
    ? rowData.find((row) => row.id === qid)
    : {}
  const [_choices, setChoicesData] = useState([])
  const [_statement, setStatement] = useState(statement)
  const [_point, setPoint] = useState(point)
  const [_feedback, setFeedback] = useState({
    correct: feedback?.correct ?? '',
    incorrect: feedback?.incorrect ?? '',
  })
  const gridRef = useRef()
  const [rowCount, setRowCount] = useState(0)
  const [gradingMethod, setGradingMethod] = useState(gradingMethods[0])
  const [gradingMethodQuery, setGradingMethodQuery] = useState('')
  const defaultColDef = useMemo(() => {
    return {
      editable: true,
      resizable: true,
    }
  }, [])

  // Store a local copy of `statement`
  useEffect(() => {
    if (typeof statement === 'string') {
      setStatement(statement)
    }
  }, [statement])
  // Store a local copy of `point`
  useEffect(() => {
    if (!isNaN(parseInt(point))) {
      setPoint(point.toString())
    }
  }, [point])
  // Store a local copy of `choices`
  useEffect(() => {
    if (Array.isArray(choices)) {
      setChoicesData(choices)
    }
  }, [choices])
  // Store a local copy of `feedback`
  useEffect(() => {
    if (feedback) {
      setFeedback({
        correct: feedback?.correct ?? '',
        incorrect: feedback?.incorrect ?? '',
      })
    }
  }, [feedback])

  const refreshCells = useCallback(() => {
    if (!open) {
      return
    }

    const params = {
      force: true,
      suppressFlash: true,
    }
    gridRef.current?.api?.refreshCells(params)
  }, [open])

  const getRowId = useCallback((params) => params.data.id, [])

  // Auto resize columns based on their content
  const onFirstDataRendered = useCallback((e) => {
    let allColumnIds = []

    e.columnApi.getColumns().forEach((column) => {
      if (!['text', 'correct'].includes(column.getId())) {
        allColumnIds.push(column.getId())
      }
    })
    e.columnApi.autoSizeColumns(allColumnIds)
  }, [])

  const onRowDragEnd = useCallback(() => {
    refreshCells()
  }, [refreshCells])

  const onRowDataUpdated = useCallback(() => {
    refreshCells()

    // Update row count state
    setRowCount(gridRef.current?.api?.getDisplayedRowCount() ?? 0)
  }, [refreshCells])

  const handleConfirm = () => {
    let newRowData = []
    gridRef.current.api.forEachNode((node) => {
      newRowData.push(node.data)
    })

    // Pass the updated data to onConfirm() callback
    onConfirm({
      id,
      statement: _statement,
      point: parseInt(_point),
      gradingMethod: gradingMethod?.id ?? '',
      choices: newRowData,
      feedback: _feedback,
    })
  }

  const handleCorrectChange = useCallback((e) => {
    let newChoicesData = []

    gridRef.current.api.forEachNode((node) => {
      if (node.data.id === e.target.id) {
        newChoicesData.push({ ...node.data, correct: true })
      } else {
        newChoicesData.push({ ...node.data, correct: false })
      }
    })

    setChoicesData(newChoicesData)
  }, [])

  /**
   * Handle point input change
   *
   * @param {*} e - Event object
   */
  const handlePointChange = (e) => {
    setPoint(e.target.value)
  }

  const addRow = useCallback((params) => {
    const dummyRowData = generateDummyRowData()
    const atIndex = params ? params.rowIndex + 1 : 0
    const res = gridRef.current.api.applyTransaction({
      add: [dummyRowData],
      addIndex: atIndex,
    })

    // Turn on row editing after the animation is finished (~250ms)
    setTimeout(() => {
      gridRef.current.api.startEditingCell({
        rowIndex: res.add[0]?.rowIndex,
        // Place the cursor in the choice `text` column
        colKey: 'text',
      })
    }, 250)
  }, [])

  const columnDefs = useMemo(
    () => [
      {
        field: 'action',
        editable: false,
        suppressSizeToFit: true,
        cellRenderer: (params) => {
          return (
            <AgGridActionCellRenderer
              {...params}
              generateDummyRowData={generateDummyRowData}
              editColKey="text"
            />
          )
        },
      },
      {
        field: 'correct',
        editable: false,
        width: 100,
        cellRenderer: (params) => (
          <input
            id={params.data.id}
            type="radio"
            checked={params.data.correct}
            onChange={handleCorrectChange}
          />
        ),
      },
      {
        field: 'text',
        headerName: 'Choice',
        flex: 1,
      },
    ],
    [handleCorrectChange]
  )

  const filteredGradingMethods =
    gradingMethodQuery === ''
      ? gradingMethods
      : gradingMethods.filter((method) => {
          return method.name
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(gradingMethodQuery.toLowerCase().replace(/\s+/g, ''))
        })

  return (
    <>
      <Transition appear show={open} as={Fragment}>
        <Dialog as="div" className="relative z-40" onClose={() => null}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className={clsx(
                    'group w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-sm transition-all',
                    inProgress && 'inProgress'
                  )}
                >
                  <Dialog.Title
                    as="h3"
                    className="mb-5 text-xl font-bold leading-none text-black-olive"
                  >
                    {title}
                  </Dialog.Title>
                  <div className="mt-2">
                    <div
                      className="text-sm"
                      dangerouslySetInnerHTML={{ __html: text }}
                    />
                  </div>

                  <div className="mb-10 flex flex-row items-stretch space-x-6">
                    {/** Question statement */}
                    <div className="flex flex-1 flex-col">
                      <h4 className="mb-1">
                        <strong>Question</strong>
                      </h4>
                      <div className="flex-1 rounded-[8px] border-[0px] border-gainsboro px-3 py-2 shadow-sm">
                        <EditableComponent
                          editable={true}
                          doc={_statement ?? '<div></div>'}
                          setDoc={setStatement}
                          editableClassName=""
                          scrollbarY={false}
                          registerChange={false}
                        />
                      </div>
                    </div>

                    <div className="flex flex-col space-y-6">
                      {/** Point */}
                      <div>
                        <h4 className="mb-1">
                          <strong>Point</strong>
                        </h4>
                        <input
                          className="w-full rounded-[8px] border-[0px] border-gainsboro text-xs shadow-sm"
                          type="number"
                          placeholder="120"
                          value={_point}
                          onChange={handlePointChange}
                        />
                      </div>

                      {/** Grading method */}
                      <div>
                        <h4 className="mb-1">
                          <strong>Grading Method</strong>
                        </h4>
                        <div className="relative mt-1">
                          <Combobox
                            value={gradingMethod}
                            onChange={setGradingMethod}
                          >
                            <div className="relative w-full cursor-default overflow-hidden rounded-[8px] bg-white text-left shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-bright-gray focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm">
                              <Combobox.Input
                                className="w-full border-none py-2 pl-3 pr-10 text-xs leading-5 text-dark-charcoal focus:ring-0"
                                displayValue={(method) => method.name}
                                onChange={(event) =>
                                  setGradingMethodQuery(event.target.value)
                                }
                              />
                              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon
                                  className="h-5 w-5 text-gray-400"
                                  aria-hidden="true"
                                />
                              </Combobox.Button>
                            </div>
                            <Transition
                              as={Fragment}
                              leave="transition ease-in duration-100"
                              leaveFrom="opacity-100"
                              leaveTo="opacity-0"
                              afterLeave={() => setGradingMethodQuery('')}
                            >
                              <Combobox.Options className="text-base absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-[8px] bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                {filteredGradingMethods.length === 0 &&
                                gradingMethodQuery !== '' ? (
                                  <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                                    Nothing found.
                                  </div>
                                ) : (
                                  filteredGradingMethods.map((method) => (
                                    <Combobox.Option
                                      key={method.id}
                                      className={({ active }) =>
                                        `relative cursor-default select-none py-2 pl-10 pr-4 text-xs ${
                                          active
                                            ? 'bg-skin-fill text-white'
                                            : 'text-gray-900'
                                        }`
                                      }
                                      value={method}
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <span
                                            className={`block truncate ${
                                              selected
                                                ? 'font-medium'
                                                : 'font-normal'
                                            }`}
                                          >
                                            {method.name}
                                          </span>
                                          {selected ? (
                                            <span
                                              className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                                active
                                                  ? 'text-white'
                                                  : 'text-skin-fill'
                                              }`}
                                            >
                                              <CheckIcon
                                                className="h-5 w-5"
                                                aria-hidden="true"
                                              />
                                            </span>
                                          ) : null}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))
                                )}
                              </Combobox.Options>
                            </Transition>
                          </Combobox>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/** Choice list */}
                  <div className="mb-10">
                    <h4 className="mb-1">
                      <strong>Choices</strong>
                    </h4>
                    <div className="ag-theme-alpine relative h-[35vh] w-full shadow-sm">
                      <AgGridReact
                        ref={gridRef}
                        rowData={_choices}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        editType={'fullRow'}
                        stopEditingWhenCellsLoseFocus={true}
                        animateRows={true}
                        rowDragManaged={true}
                        suppressNoRowsOverlay={true}
                        getRowId={getRowId}
                        onFirstDataRendered={onFirstDataRendered}
                        onRowDragEnd={onRowDragEnd}
                        onRowDataUpdated={onRowDataUpdated}
                      ></AgGridReact>
                      {rowCount === 0 && (
                        <div className="absolute left-1/2 top-1/2 flex h-full w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center backdrop-blur-sm">
                          <button
                            onClick={() => addRow()}
                            className="group/button flex items-center space-x-1 text-sm active:translate-y-[0.75px] active:scale-[0.99]"
                          >
                            <PlusCircleIcon className="h-[30px] w-[30px] fill-dark-charcoal/80 transition-all group-hover/button:fill-dark-charcoal/100" />
                            <span className="font-bold text-dark-charcoal/80 transition-all group-hover/button:text-dark-charcoal/100">
                              Click to add a new choice
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/** Feedback */}
                  <div className="flex items-stretch justify-between space-x-6">
                    <div className="flex flex-1 flex-col">
                      <h4 className="mb-1">
                        <strong>Correct Feedback</strong>
                      </h4>
                      <div className="flex-1 rounded-[8px] border-[0px] border-gainsboro bg-green/20 px-3 py-2 shadow-sm">
                        <EditableComponent
                          editable={true}
                          doc={_feedback?.correct ?? '<div></div>'}
                          setDoc={(newValue) =>
                            setFeedback({
                              ..._feedback,
                              correct: newValue ?? '',
                            })
                          }
                          editableClassName=""
                          scrollbarY={false}
                          registerChange={false}
                        />
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col">
                      <h4 className="mb-1">
                        <strong>Incorrect Feedback</strong>
                      </h4>
                      <div className="flex-1 rounded-[8px] border-[0px] border-gainsboro bg-red-200 px-3 py-2 shadow-sm">
                        <EditableComponent
                          editable={true}
                          doc={_feedback?.incorrect ?? '<div></div>'}
                          setDoc={(newValue) =>
                            setFeedback({
                              ..._feedback,
                              incorrect: newValue ?? '',
                            })
                          }
                          editableClassName=""
                          scrollbarY={false}
                          registerChange={false}
                        />
                      </div>
                    </div>
                  </div>

                  {/** Dialog button */}
                  <div className="mt-6 text-right">
                    <button
                      type="button"
                      className="rounded-full border-2 border-skin-button-accent bg-skin-button-accent px-6 py-2 text-xs leading-none text-white transition-colors group-[.inProgress]:cursor-not-allowed group-[.inProgress]:opacity-50 group-[:not(.inProgress)]:active:translate-y-[0.75px] group-[:not(.inProgress)]:active:scale-[0.99]"
                      disabled={inProgress}
                      onClick={handleConfirm}
                    >
                      {inProgress ? inProgressButtonText : yesText}
                    </button>
                    <button
                      type="button"
                      className="ml-2 rounded-full border-2 border-skin-button-accent px-6 py-2 text-xs leading-none transition-colors group-[.inProgress]:cursor-not-allowed group-[.inProgress]:opacity-50 group-[:not(.inProgress)]:hover:bg-skin-button-accent group-[:not(.inProgress)]:hover:text-white group-[:not(.inProgress)]:active:translate-y-[0.75px] group-[:not(.inProgress)]:active:scale-[0.99]"
                      disabled={inProgress}
                      onClick={() => onCancel()}
                    >
                      {noText}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

const generateDummyRowData = () => {
  return {
    id: uuidv4(),
    correct: false,
    text: 'New choice',
  }
}

export default DialogEditKnowledgeCheck
